import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./ExecutiveOverview.css";
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  Tabs,
  Table,
  ColumnLayout,
  Select,
  SelectProps,
  Multiselect,
  MultiselectProps,
  FormField,
  Button,
  ButtonDropdown,
  Pagination,
  SegmentedControl,
  Icon,
  Popover,
  CollectionPreferences,
  Spinner,
  Alert,
} from "@cloudscape-design/components";
import { TimeRange, TimeRangeValue } from "../../components/TimeRange";
import Plot from "react-plotly.js";
import Program from "../Program/Program";
import { RootState } from "../../redux/reducers/rootReducer";
import { getCMPerformanceRequest, getPHICTrendFiltersRequest, getPHICTrendDataRequest } from "../../redux/actions/executiveOverviewActions";
import {
  CMPerformancePeriod,
  PHICTrendFilterCombination,
  PHICTrendGroupBy,
  PHICTrendSeriesItem,
} from "../../redux/types/executiveOverviewTypes";
import { usePermission } from "../../hooks/usePermission";

// ========================
// Types
// ========================
type RiskStatus = "red" | "yellow" | "green";


interface MetricsRow {
  operation: string;
  // Unique per-row identity (operation + cmSite). cmSite alone is NOT unique —
  // the same CM-Location can appear under multiple operations.
  rowKey: string;
  cmSite: string;
  cm: string;
  cm_location: string;
  output_hit_rate: RiskStatus;
  machine_downtime: RiskStatus;
  test_fpy: RiskStatus;
  assembly_fpy: RiskStatus;
}

interface PHICRecord {
  date: string;
  cm: string;
  cm_location: string;
  operation: string;
  program: string;
  stage: string;
  alert_type: string;
  alert_reason: string;
  status: "new" | "open" | "acknowledged" | "closed";
  cause_type: string;
  dri: string;
  station: string;
}



// ========================
// Helper: Risk Indicator
// ========================
const RISK_DOT_COLORS: Record<RiskStatus, string> = {
  green: "#1d8102",
  yellow: "#d4a800",
  red: "#d13212",
};

const RiskDot: React.FC<{ status: RiskStatus }> = ({ status }) => (
  <span
    style={{
      display: "inline-block",
      width: 18,
      height: 18,
      borderRadius: "50%",
      backgroundColor: RISK_DOT_COLORS[status],
    }}
  />
);

// Mapping from metrics table column keys to PHIC category display names
const COLUMN_TO_CATEGORY: Record<string, string> = {
  output_hit_rate: "Output Hit Rate",
  machine_downtime: "Machine Downtime",
  test_fpy: "Test FPY",
  assembly_fpy: "Assembly FPY",
};

// Helper: Derive the display alert type from backend data
// Backend returns alert_type="FPY Goal" for both Test FPY and Assembly FPY,
// distinguished by stage: "Testing" → "Test FPY", "Assembly" → "Assembly FPY"
const getDisplayAlertType = (record: PHICRecord): string => {
  if (record.alert_type === "FPY Goal") {
    if (record.stage === "Testing") return "Test FPY";
    if (record.stage === "Assembly") return "Assembly FPY";
    return "FPY Goal"; // fallback
  }
  return record.alert_type;
};

// Helper: Check if a PHIC record matches a given display category
const matchesCategory = (record: PHICRecord, category: string): boolean => {
  return getDisplayAlertType(record) === category;
};

// Tooltip descriptions for each metric column
interface TooltipRule {
  green: string;
  yellow: string;
  red: string;
}

const COLUMN_TOOLTIP: Record<string, TooltipRule> = {
  output_hit_rate: {
    green: "No PHIC records exist, or all PHIC records are Closed.",
    yellow: "PHIC records exist with status = Acknowledged (no New/Open).",
    red: "At least one PHIC record has status = New or Open."
  },
  machine_downtime: {
    green: "No PHIC records exist, or all PHIC records are Closed.",
    yellow: "PHIC records exist with status = Acknowledged (no New/Open).",
    red: "At least one PHIC record has status = New or Open."
  },
  test_fpy: {
    green: "No PHIC records exist, or all PHIC records are Closed.",
    yellow: "PHIC records exist with status = Acknowledged (no New/Open).",
    red: "At least one PHIC record has status = New or Open."
  },
  assembly_fpy: {
    green: "No PHIC records exist, or all PHIC records are Closed.",
    yellow: "PHIC records exist with status = Acknowledged (no New/Open).",
    red: "At least one PHIC record has status = New or Open."
  },
};

// Helper: Question mark icon with hover tooltip
const TooltipIcon: React.FC<{ tooltip: TooltipRule; testId?: string }> = ({ tooltip, testId }) => (
  <span data-testid={testId} style={{ marginLeft: "4px", cursor: "pointer", display: "inline-flex", verticalAlign: "middle", fontSize: "10px" }}>
    <Popover
      dismissButton={false}
      position="top"
      size="medium"
      triggerType="custom"
      content={
        <div data-testid="tooltip-popover-content" style={{ fontSize: "13px", lineHeight: "1.6" }}>
          <div style={{ display: "flex", alignItems: "start", gap: "6px" }}>
            <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: "#1d8102", flexShrink: 0 }} /> {tooltip.green}</span>
          </div>
          <div style={{ display: "flex", alignItems: "start", gap: "6px", marginTop: "4px" }}>
            <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: "#d4a800", flexShrink: 0 }} /> {tooltip.yellow}</span>
          </div>
          <div style={{ display: "flex", alignItems: "start", gap: "6px", marginTop: "4px" }}>
            <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: "#d13212", flexShrink: 0 }} /> {tooltip.red}</span>
          </div>
        </div>
      }
    >
      <span style={{ cursor: "pointer" }}>
        <Icon name="status-info" variant="subtle" size="small" />
      </span>
    </Popover>
  </span>
);

// Helper: get the ISO 8601 week number
// ISO 8601: weeks start on Monday, week 1 is the week containing the first Thursday of the year
const getWeekNumber = (date?: Date): number => {
  const d = new Date(Date.UTC((date || new Date()).getFullYear(), (date || new Date()).getMonth(), (date || new Date()).getDate()));
  // Set to nearest Thursday: current date + 4 - current day number (Monday=1, Sunday=7)
  const dayNum = d.getUTCDay() || 7; // Make Sunday = 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  // Get first day of year
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  // Calculate full weeks to nearest Thursday
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

const getCurrentMonthNumber = (): number => new Date().getMonth() + 1;

const getTimePeriodLabel = (timePeriod: string): string => {
  const now = new Date();
  const year = now.getFullYear();
  const currentWeek = getWeekNumber(now);
  const currentMonth = getCurrentMonthNumber();

  switch (timePeriod) {
    case "last_week": {
      let lastWeekNum = currentWeek - 1;
      let lastWeekYear = year;
      if (lastWeekNum <= 0) {
        lastWeekYear = year - 1;
        lastWeekNum = getWeekNumber(new Date(lastWeekYear, 11, 28));
      }
      return `${lastWeekYear}-Week${String(lastWeekNum).padStart(2, "0")}`;
    }
    case "current_week":
      return `${year}-Week${String(currentWeek).padStart(2, "0")}`;
    case "last_month": {
      let lastMonth = currentMonth - 1;
      let lastMonthYear = year;
      if (lastMonth <= 0) { lastMonth = 12; lastMonthYear = year - 1; }
      const MONTH_LABELS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      return `${lastMonthYear}-${MONTH_LABELS[lastMonth - 1]}`;
    }
    case "current_month": {
      const MONTH_LABELS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      return `${year}-${MONTH_LABELS[currentMonth - 1]}`;
    }
    default:
      return `${year}-Week${String(currentWeek).padStart(2, "0")}`;
  }
};


// Helper: Get current date in ISO format for API request
const getCurrentDateISO = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

// ========================
// Tab 1: CM Performance
// ========================
const CMPerformanceTab: React.FC<{ onNavigateToProgram?: (params: Record<string, string>) => void }> = ({ onNavigateToProgram }) => {
  const dispatch = useDispatch();
  const { hasDataPermission } = usePermission();
  const { data: cmPerformanceData, loading, error } = useSelector(
    (state: RootState) => state.executiveOverview.cmPerformance
  );

  const [timePeriod, setTimePeriod] = useState<CMPerformancePeriod>("current_week");
  // Selection is keyed by rowKey (operation + cmSite) so the same CM-Location under
  // different operations is treated as distinct rows and only one cell highlights.
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string | null>(null);

  // Auto-dismiss error alert after 10 seconds
  const [showError, setShowError] = useState(false);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (error) {
      setShowError(true);
      // Clear any existing timer
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
      // Auto-dismiss after 10 seconds
      errorTimerRef.current = setTimeout(() => {
        setShowError(false);
      }, 10000);
    } else {
      setShowError(false);
    }
    return () => {
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
    };
  }, [error]);

  const METRICS_PAGE_SIZE = 10;
  const [metricsCurrentPage, setMetricsCurrentPage] = useState(1);
  const PHIC_PAGE_SIZE = 5;
  const [phicCurrentPage, setPhicCurrentPage] = useState(1);
  const [phicPageSize, setPhicPageSize] = useState(PHIC_PAGE_SIZE);

  // PHIC table column visibility preferences
  // Columns: program, cm, location, category are always visible (cannot be hidden)
  const PHIC_CONTENT_DISPLAY_DEFAULT: ReadonlyArray<{ id: string; visible: boolean }> = [
    { id: "date", visible: true },
    { id: "operation", visible: true },
    { id: "cm", visible: true },
    { id: "location", visible: true },
    { id: "program", visible: true },
    { id: "alert_type", visible: true },
    { id: "stage", visible: true },
    { id: "alert_reason", visible: true },
    { id: "status", visible: true },
  ];
  const [phicColumnDisplay, setPhicColumnDisplay] = useState<ReadonlyArray<{ id: string; visible: boolean }>>(PHIC_CONTENT_DISPLAY_DEFAULT);

  // ========================
  // Dispatch getCMPerformanceRequest API request when timePeriod changes
  // ========================
  useEffect(() => {
    dispatch(
      getCMPerformanceRequest({
        period: timePeriod,
        date: getCurrentDateISO(),
      })
    );
  }, [dispatch, timePeriod]);

  // Merge duplicate cmSite entries: red > yellow > green (worst wins)
  const metricsData: MetricsRow[] = useMemo(() => {
    if (cmPerformanceData?.risk_table) {
      // Helper: merge two risk statuses — red > yellow > green
      const mergeStatus = (a: RiskStatus, b: RiskStatus): RiskStatus => {
        if (a === "red" || b === "red") return "red";
        if (a === "yellow" || b === "yellow") return "yellow";
        return "green";
      };

      const mergedMap = new Map<string, MetricsRow>();
      for (const item of cmPerformanceData.risk_table) {
        const key = `${item.cm}-${item.cm_location}-${item.operation}`;
        const cmSite = `${item.cm}-${item.cm_location}`;
        const current: MetricsRow = {
          operation: item.operation,
          rowKey: key,
          cmSite,
          cm: item.cm,
          cm_location: item.cm_location,
          output_hit_rate: item.indicators.output_hit_rate as RiskStatus,
          machine_downtime: item.indicators.machine_downtime as RiskStatus,
          test_fpy: item.indicators.test_fpy as RiskStatus,
          assembly_fpy: item.indicators.assembly_fpy as RiskStatus,
        };

        if (mergedMap.has(key)) {
          const existing = mergedMap.get(key)!;
          existing.output_hit_rate = mergeStatus(existing.output_hit_rate, current.output_hit_rate);
          existing.machine_downtime = mergeStatus(existing.machine_downtime, current.machine_downtime);
          existing.test_fpy = mergeStatus(existing.test_fpy, current.test_fpy);
          existing.assembly_fpy = mergeStatus(existing.assembly_fpy, current.assembly_fpy);
        } else {
          mergedMap.set(key, current);
        }
      }
      // Filter out Compal-Kunshan and CNT-India
      const EXCLUDED_CM_SITES = [
        { cm: "Compal", cm_location: "Kunshan" },
        { cm: "CNT", cm_location: "India" },
      ];
      const filtered = Array.from(mergedMap.values())
        .filter(
          (row) => !EXCLUDED_CM_SITES.some(
            (excluded) => row.cm === excluded.cm && row.cm_location === excluded.cm_location
          )
        );
      // Group by operation so same operations are adjacent
      filtered.sort((a, b) => a.operation.localeCompare(b.operation));
      return filtered;
    }
    return [];
  }, [cmPerformanceData, timePeriod]);

  const phicRecords: PHICRecord[] = useMemo(() => {
    if (cmPerformanceData?.phic_records) {
      return cmPerformanceData.phic_records as PHICRecord[];
    }
    return [];
  }, [cmPerformanceData]);

  const headerTitle = useMemo(() => getTimePeriodLabel(timePeriod), [timePeriod]);

  const selectedCategory = selectedColumn ? COLUMN_TO_CATEGORY[selectedColumn] || null : null;

  // The currently selected row (if any), resolved from the unique rowKey.
  const selectedRow = useMemo(
    () => (selectedRowKey ? metricsData.find((r) => r.rowKey === selectedRowKey) ?? null : null),
    [selectedRowKey, metricsData]
  );
  // Display label for the selected CM-Location (may be shared across operations).
  const selectedCmSite = selectedRow?.cmSite ?? null;

  const recordMatchesSelectedRow = useMemo(() => {
    return (r: PHICRecord): boolean => {
      if (!selectedRow) return true;
      return r.cm === selectedRow.cm && r.cm_location === selectedRow.cm_location && r.operation === selectedRow.operation;
    };
  }, [selectedRow]);

  // Filter PHIC records by selected filters
  const filteredPHICRecords = useMemo(() => {
    let records = phicRecords;
    if (selectedRow) {
      records = records.filter(recordMatchesSelectedRow);
    }
    if (selectedCategory) {
      records = records.filter((r) => matchesCategory(r, selectedCategory));
    }
    if (selectedStatusFilter) {
      records = records.filter((r) => r.status === selectedStatusFilter);
    }
    return records;
  }, [phicRecords, selectedRow, recordMatchesSelectedRow, selectedCategory, selectedStatusFilter]);

  // Status counts
  const statusCounts = useMemo(() => {
    let records = phicRecords;
    if (selectedRow) {
      records = records.filter(recordMatchesSelectedRow);
    }
    if (selectedCategory) {
      records = records.filter((r) => matchesCategory(r, selectedCategory));
    }
    return {
      new: records.filter((r) => r.status === "new").length,
      open: records.filter((r) => r.status === "open").length,
      acknowledged: records.filter((r) => r.status === "acknowledged").length,
      closed: records.filter((r) => r.status === "closed").length,
    };
  }, [phicRecords, selectedRow, recordMatchesSelectedRow, selectedCategory]);

  const handleMetricsCellClick = (rowKey: string, column: string) => {
    if (selectedRowKey === rowKey && selectedColumn === column) {
      setSelectedRowKey(null);
      setSelectedColumn(null);
    } else {
      setSelectedRowKey(rowKey);
      setSelectedColumn(column);
    }
    setSelectedStatusFilter(null);
    setPhicCurrentPage(1);
  };

  const handleStatusCardClick = (status: string) => {
    setSelectedStatusFilter((prev) => (prev === status ? null : status));
    setPhicCurrentPage(1);
  };

  const phicColumns = [
    { id: "date", header: "Date", cell: (item: PHICRecord) => item.date, sortingField: "date" },
    { id: "operation", header: "Operation", cell: (item: PHICRecord) => item.operation },
    { id: "cm", header: "CM", cell: (item: PHICRecord) => item.cm },
    { id: "location", header: "Location", cell: (item: PHICRecord) => item.cm_location },
    { id: "program", header: "Program", cell: (item: PHICRecord) => (
      <span style={{ color: "#0972d3", cursor: "pointer", textDecoration: "underline" }}
        onClick={() => {
          if (onNavigateToProgram) {
            const params: Record<string, string> = {};
            if (item.date) params.date = item.date;
            if (item.cm) params.cm = item.cm;
            if (item.operation) params.operation = item.operation;
            if (item.cm_location) params.location = item.cm_location;
            if (item.program) params.program = item.program;
            onNavigateToProgram(params);
          }
        }}
      >
        {item.program}
      </span>
    ) },
    { id: "stage", header: "Stage", cell: (item: PHICRecord) => item.stage },
    { id: "alert_type", header: "Alert Type", cell: (item: PHICRecord) => getDisplayAlertType(item) },
    { id: "alert_reason", header: "Alert Reason", cell: (item: PHICRecord) => item.alert_reason },
    {
      id: "status", header: "Status",
      cell: (item: PHICRecord) => {
        const cfg = statusCardConfig.find(i => i.value === item.status) || statusCardConfig[0];
        return (
          <span style={{
            display: "inline-block", padding: "3px 10px", borderRadius: "12px",
            fontSize: "12px", fontWeight: 600, backgroundColor: cfg.bgColor,
            color: cfg.color, lineHeight: "1.4", whiteSpace: "nowrap",
          }}>
            {cfg.label}
          </span>
        );
      }
    },
  ];

  const statusCardConfig = [
    { label: "New", value: "new", color: "#d13212", bgColor: "#fff5f0" },
    { label: "Open", value: "open", color: "#d13212", bgColor: "#fff5f0" },
    { label: "Acknowledged", value: "acknowledged", color: "#8D6E0A", bgColor: "#fef9e8" },
    { label: "Closed", value: "closed", color: "#1d8102", bgColor: "#f0faf0" },
  ];

  return (
    <SpaceBetween size="l">
      {/* Time Period Selector + Indicator — shared across both tables */}
      <div data-testid="time-period-selector" style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "16px",
        padding: "16px 20px",
        backgroundColor: "#f2f8fd",
        borderLeft: "5px solid #0972d3",
        borderRadius: "0 8px 8px 0",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Icon name="calendar" variant="subtle" />
          <span data-testid="time-period-label" style={{ fontSize: "20px", fontWeight: 700, color: "#0972d3" }}>
            {headerTitle}
          </span>
          <span data-testid="time-period-note" style={{ fontSize: "14px", color: "#5f6b7a", fontWeight: 400 }}>
            — Applies to both tables below
          </span>
        </div>
        <SegmentedControl
          data-testid="time-period-control"
          selectedId={timePeriod}
          onChange={({ detail }) => { setTimePeriod(detail.selectedId as CMPerformancePeriod); setMetricsCurrentPage(1); setPhicCurrentPage(1); setSelectedRowKey(null); setSelectedColumn(null); setSelectedStatusFilter(null); }}
          options={[
            { text: "Last Week", id: "last_week" },
            { text: "Current Week", id: "current_week" },
            { text: "Last Month", id: "last_month" },
            { text: "Current Month", id: "current_month" },
          ]}
        />
      </div>

      {/* Error Alert — auto-dismisses after 10s or manually via dismiss button */}
      {showError && error && (
        <Alert type="error" dismissible onDismiss={() => {
          setShowError(false);
          if (errorTimerRef.current) {
            clearTimeout(errorTimerRef.current);
            errorTimerRef.current = null;
          }
        }}>
          {error}
        </Alert>
      )}

      {/* Table 1: CM Performance */}
      <Container
        data-testid="cm-performance-container"
        header={
          <Header variant="h2" description="Click any indicator to filter PHIC records below.">
            CM Performance<span style={{marginLeft:'10px',color:'gray',fontWeight:'500'}}>({metricsData.length})</span>
          </Header>
        }
      >
        <div style={{position:'relative',marginTop:'14px'}}>
          {metricsData.length > METRICS_PAGE_SIZE && (
            <div style={{position:"absolute",top:'-34px',right:'0'}} data-testid="cm-table-pagination">
              <Pagination
                currentPageIndex={metricsCurrentPage}
                pagesCount={Math.ceil(metricsData.length / METRICS_PAGE_SIZE)}
                onChange={({ detail }) => setMetricsCurrentPage(detail.currentPageIndex)}
                ariaLabels={{
                  nextPageLabel: "Next page",
                  previousPageLabel: "Previous page",
                  pageLabel: (pageNumber) => `Page ${pageNumber}`,
                }}
              />
            </div>
          )}
          <div className="cm-table-wrapper">
            <table data-testid="cm-performance-table" className="cm-table">
              <colgroup>
                <col className="cm-table-col-operation" />
                <col className="cm-table-col-cm-location" />
                <col className="cm-table-col-metric" />
                <col className="cm-table-col-metric" />
                <col className="cm-table-col-metric" />
                <col className="cm-table-col-metric" />
              </colgroup>
              <thead>
                <tr>
                  <th rowSpan={2} className="cm-table-th-left">Operation</th>
                  <th rowSpan={2} className="cm-table-th-left">CM-Location</th>
                  <th colSpan={2} className="cm-table-th-group">
                    Delivery
                  </th>
                  <th colSpan={2} className="cm-table-th-group">
                    Quality
                  </th>
                </tr>
                <tr>
                  <th className="cm-table-th-metric">Output Hit Rate<TooltipIcon tooltip={COLUMN_TOOLTIP.output_hit_rate} testId="tooltip-icon-output" /></th>
                  <th className="cm-table-th-metric">Machine Downtime<TooltipIcon tooltip={COLUMN_TOOLTIP.machine_downtime} testId="tooltip-icon-machineDowntime" /></th>
                  <th className="cm-table-th-metric">Test FPY<TooltipIcon tooltip={COLUMN_TOOLTIP.test_fpy} testId="tooltip-icon-testFPY" /></th>
                  <th className="cm-table-th-metric">Assembly FPY<TooltipIcon tooltip={COLUMN_TOOLTIP.assembly_fpy} testId="tooltip-icon-assemblyFPY" /></th>
                </tr>
              </thead>
              <tbody>
                {/* Loading row — shown when API request is in progress */}
                {loading && (
                  <tr>
                    <td colSpan={6} className="cm-table-td-empty">
                      <div className="cm-table-td-empty-content">
                        <Spinner size="large" />
                        <span className="cm-table-loading-text">Loading...</span>
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && metricsData.length === 0 && (
                  <tr>
                    <td colSpan={6} className="cm-table-td-empty">
                      <div className="cm-table-td-empty-content">
                        <Box variant="strong">No data available</Box>
                        <Box variant="p" color="text-body-secondary">
                          {error
                            ? "Failed to load CM performance data. Please try again later."
                            : "No CM performance data available for the selected time period."}
                        </Box>
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && (() => {
                  const pageRows = metricsData.slice((metricsCurrentPage - 1) * METRICS_PAGE_SIZE, metricsCurrentPage * METRICS_PAGE_SIZE);
                  // Compute rowSpan for operation column merging
                  const operationSpanMap = new Map<number, number>();
                  let i = 0;
                  while (i < pageRows.length) {
                    let span = 1;
                    while (i + span < pageRows.length && pageRows[i + span].operation === pageRows[i].operation) {
                      span++;
                    }
                    operationSpanMap.set(i, span);
                    i += span;
                  }
                  return pageRows.map((row, idx) => {
                    const isSelectedRow = selectedRowKey === row.rowKey;
                    const isCellSelected = (colKey: string) => isSelectedRow && selectedColumn === colKey;
                    const cellStyle = (colKey: string) => ({
                      border: isCellSelected(colKey) ? "2px solid #0972d3" : "1px solid #e9ebed",
                      padding: isCellSelected(colKey) ? "11px" : "12px",
                      textAlign: "center" as const,
                      cursor: "pointer",
                      backgroundColor: isCellSelected(colKey) ? "#f0f7ff" : "transparent",
                      transition: "all 0.2s ease",
                    });
                    const showOperationCell = operationSpanMap.has(idx);
                    return (
                      <tr key={row.rowKey} data-testid={`cm-row-${row.rowKey}`}>
                        {showOperationCell && (
                          <td className="cm-table-td-label" rowSpan={operationSpanMap.get(idx)}>
                            {row.operation}
                          </td>
                        )}
                        <td className="cm-table-td-label">{row.cmSite}</td>
                        {(["output_hit_rate", "machine_downtime", "test_fpy", "assembly_fpy"] as const).map((colKey) => (
                          <td
                            key={colKey}
                            data-testid={`cm-cell-${row.rowKey}-${colKey}`}
                            style={cellStyle(colKey)}
                            onClick={() => handleMetricsCellClick(row.rowKey, colKey)}
                            onMouseEnter={(e) => {
                              if (!isCellSelected(colKey)) {
                                (e.currentTarget as HTMLElement).style.backgroundColor = "#f0f7ff";
                                (e.currentTarget as HTMLElement).style.border = "2px solid #0972d3";
                                (e.currentTarget as HTMLElement).style.padding = "11px";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isCellSelected(colKey)) {
                                (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
                                (e.currentTarget as HTMLElement).style.border = "1px solid #e9ebed";
                                (e.currentTarget as HTMLElement).style.padding = "12px";
                              }
                            }}
                          >
                            <RiskDot status={row[colKey]} />
                          </td>
                        ))}
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
          {selectedCmSite && selectedColumn && (
            <Box margin={{ top: "xs" }}>
              <Button data-testid="clear-cell-filter" variant="link" onClick={() => { setSelectedRowKey(null); setSelectedColumn(null); }}>
                Clear cell filter: {selectedRow?.operation ? `${selectedRow.operation} / ` : ""}{selectedCmSite} / {COLUMN_TO_CATEGORY[selectedColumn]}
              </Button>
            </Box>
          )}
        </div>
      </Container>

      {/* Table 2: PHIC Status Summary */}
      <Container
        data-testid="phic-summary-container"
        header={
          <Header
            variant="h2"
            description={
              <span data-testid="phic-summary-description">
                {selectedCmSite && selectedCategory
                  ? `Filtered by: ${selectedRow?.operation ? `${selectedRow.operation} / ` : ""}${selectedCmSite} / ${selectedCategory}${selectedStatusFilter ? ` / ${selectedStatusFilter}` : ""}`
                  : selectedStatusFilter
                  ? `Filtered by status: ${selectedStatusFilter}`
                  : "Overview of all PHIC items"}
              </span>
            }
          >
            PHIC Status Summary <span style={{marginLeft:'4px',color:'gray',fontWeight:'500'}}>({filteredPHICRecords.length})</span>
          </Header>
        }
      >
        <SpaceBetween size="l">
          <ColumnLayout columns={4} variant="text-grid">
            {statusCardConfig.map(({ label, value, color, bgColor }) => (
                <div
                  key={value}
                  data-testid={`status-card-${value}`}
                  onClick={() => handleStatusCardClick(value)}
                  onMouseEnter={(e) => {
                    if (selectedStatusFilter !== value) {
                      (e.currentTarget as HTMLElement).style.border = `2px solid ${color}`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedStatusFilter !== value) {
                      (e.currentTarget as HTMLElement).style.border = "2px solid transparent";
                    }
                  }}
                  style={{
                    cursor: "pointer", padding: "16px 20px", borderRadius: "8px",
                    border: selectedStatusFilter === value ? `2px solid ${color}` : "2px solid transparent",
                    backgroundColor: bgColor, textAlign: "center", transition: "all 0.2s ease",
                  }}
                >
                  <Box variant="awsui-key-label">{label}</Box>
                  <Box variant="h1" padding={{ top: "xxs" }}>
                    <span data-testid={`status-count-${value}`} style={{ color }}>{statusCounts[value as keyof typeof statusCounts]}</span>
                  </Box>
                </div>
              ))}
            </ColumnLayout>

            <Table
              data-testid="phic-detail-table"
              columnDefinitions={phicColumns}
              visibleColumns={phicColumnDisplay.filter(c => c.visible).map(c => c.id)}
              items={loading ? [] : filteredPHICRecords.slice((phicCurrentPage - 1) * phicPageSize, phicCurrentPage * phicPageSize)}
              loading={loading}
              loadingText="Loading PHIC records..."
              variant="embedded"
              stripedRows
              sortingDisabled
              pagination={
                <Pagination
                  data-testid="phic-detail-pagination"
                  currentPageIndex={phicCurrentPage}
                  pagesCount={Math.max(1, Math.ceil(filteredPHICRecords.length / phicPageSize))}
                  onChange={({ detail }) => setPhicCurrentPage(detail.currentPageIndex)}
                  ariaLabels={{
                    nextPageLabel: "Next page",
                    previousPageLabel: "Previous page",
                    pageLabel: (pageNumber) => `Page ${pageNumber}`,
                  }}
                />
              }
              preferences={
                <CollectionPreferences
                  data-testid="phic-detail-preferences"
                  title="PHIC Table Preferences"
                  confirmLabel="Confirm"
                  cancelLabel="Cancel"
                  preferences={{
                    pageSize: phicPageSize,
                    contentDisplay: phicColumnDisplay as any,
                  }}
                  pageSizePreference={{
                    title: "Page size",
                    options: [
                      { value: 5, label: "5 items" },
                      { value: 10, label: "10 items" },
                      { value: 20, label: "20 items" },
                    ],
                  }}
                  contentDisplayPreference={{
                    title: "Column preferences",
                    description: "Customize which columns are visible. CM, Operation, Location, Program, and Alert Type cannot be hidden.",
                    options: [
                      { id: "date", label: "Date", alwaysVisible: false },
                      { id: "operation", label: "Operation", alwaysVisible: true },
                      { id: "cm", label: "CM", alwaysVisible: true },
                      { id: "location", label: "Location", alwaysVisible: true },
                      { id: "program", label: "Program", alwaysVisible: true },
                      { id: "alert_type", label: "Alert Type", alwaysVisible: true },
                      { id: "stage", label: "Stage", alwaysVisible: false },
                      { id: "alert_reason", label: "Alert Reason", alwaysVisible: false },
                      { id: "status", label: "Status", alwaysVisible: false },
                    ],
                  }}
                  onConfirm={({ detail }) => {
                    if (detail.pageSize) {
                      setPhicPageSize(detail.pageSize);
                      setPhicCurrentPage(1);
                    }
                    if (detail.contentDisplay) {
                      setPhicColumnDisplay(detail.contentDisplay);
                    }
                  }}
                />
              }
              empty={
                <Box textAlign="center" color="inherit" padding="l">
                  <b>No records</b>
                  <Box variant="p" color="inherit">
                    {(selectedCmSite || selectedCategory || selectedStatusFilter)
                      ? "No PHIC records match the current filters."
                      : "No PHIC records available for the selected time period."}
                  </Box>
                </Box>
              }
            />
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

// ========================
// Tab 2: PHIC Trend
// ========================

const MAX_VISIBLE_MONTHS = 12;

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const formatMonthLabel = (ym: string): string => {
  const [year, month] = ym.split("-");
  return `${MONTH_NAMES[parseInt(month, 10) - 1]} ${year}`;
};

// Default time range: last 12 months (YYYY-MM format)
const getDefaultTimeRange = (): { start: string; end: string } => {
  const now = new Date();
  const end = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const d = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  const start = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  return { start, end };
};

// Color palette options for chart
const COLOR_PALETTES: Record<string, { label: string; colors: string[] }> = {
  tableau: {
    label: "Tableau Classic",
    colors: [
      "#4E79A7", "#F28E2B", "#E15759", "#76B7B2", "#59A14F",
      "#EDC948", "#B07AA1", "#FF9DA7", "#9C755F", "#BAB0AC",
      "#A0CBE8", "#FFBE7D", "#8CD17D", "#B6992D", "#F1CE63",
      "#499894", "#86BCB6", "#D4A6C8", "#D7B5A6", "#79706E",
      "#AF7AA1", "#E15759", "#FF9D9A", "#86BCB6", "#D37295",
    ],
  },
  morandi: {
    label: "Morandi Muted",
    colors: [
      "#7BA1B0", "#B5927E", "#9CAFA4", "#C4A883", "#8E7F9E",
      "#C09B97", "#89A8A0", "#BFADA3", "#A5B5A8", "#D4B8A0",
      "#6E8B9E", "#C4ADA0", "#9EB5AD", "#B8A090", "#A89EB5",
      "#C0B5A8", "#7F9EA8", "#D4C4B0", "#A0B8B0", "#B5A090",
      "#8EA0A8", "#C4B8A8", "#A8C0B0", "#D0B8A0", "#9090A8",
    ],
  },
  fresh: {
    label: "Fresh Pastel",
    colors: [
      "#93c4e0", "#f89fa1", "#7ad38c", "#fed392", "#c0aee4",
      "#7bdae2", "#fdba7b", "#90dcc3", "#E8A0B8", "#d6eb84",
      "#7bd6e6", "#f9a46a", "#9aeac2", "#f0bd9b", "#A0B8E0",
      "#e1eaa7", "#80C8D8", "#F8D0B8", "#B0E0D0", "#f5cde1",
      "#68d4de", "#F0D0A8", "#A0D8C0", "#E8B8A0", "#90B0D8",
    ],
  },
  nordic: {
    label: "Nordic Minimal",
    colors: [
      "#2E5266", "#6E8898", "#9FB1BC", "#D3D0CB", "#E2C044",
      "#3E7B91", "#8BA8B5", "#4A7C6F", "#C49A6C", "#5B7065",
      "#3D5A6E", "#7A9BA8", "#5C8C7C", "#B89060", "#6B8075",
      "#4E6A7E", "#8CAAB8", "#6C9C8C", "#A88050", "#7B9085",
      "#2E4A5E", "#6A8A98", "#4C7C6C", "#987040", "#5B7065",
    ],
  },
};

const PALETTE_OPTIONS: SelectProps.Option[] = Object.entries(COLOR_PALETTES).map(([value, { label }]) => ({
  label,
  value,
}));

const GROUP_BY_OPTIONS: { label: string; value: PHICTrendGroupBy }[] = [
  { label: "Program", value: "program" },
  { label: "Cause Type", value: "cause_type" },
  { label: "Alert Type", value: "alert_type" },
];


// "All" option constant for Multiselect filters
const ALL_OPTION: MultiselectProps.Option = { label: "All", value: "__all__" };

// Helper: handle Multiselect onChange with "All" option
// Behavior:
// - When filters are empty (no selection), all data is displayed (no filtering).
// - Clicking "All" when nothing or partial items are selected → select all items.
// - Clicking "All" when all items are already selected → deselect all (clear).
// - User can select/deselect individual items freely.
const handleMultiselectWithAll = (
  selectedOptions: readonly MultiselectProps.Option[],
  allAvailableOptions: readonly MultiselectProps.Option[],
  previousSelection: readonly MultiselectProps.Option[],
  setter: (opts: readonly MultiselectProps.Option[]) => void
) => {
  const hadAll = previousSelection.some(o => o.value === "__all__");
  const nowHasAll = selectedOptions.some(o => o.value === "__all__");

  if (nowHasAll && !hadAll) {
    // User just clicked "All" → select all available items (with ALL_OPTION included to track state)
    setter([ALL_OPTION, ...allAvailableOptions]);
  } else if (hadAll && !nowHasAll) {
    // User deselected "All" while all items were selected → clear all
    setter([]);
  } else if (hadAll && nowHasAll) {
    // User deselected an individual item while "All" was selected → remove ALL_OPTION and the deselected item
    const nonAllOptions = selectedOptions.filter(o => o.value !== "__all__");
    setter(nonAllOptions);
  } else {
    // Normal selection/deselection of individual items (All not involved)
    const nonAllOptions = selectedOptions.filter(o => o.value !== "__all__");
    // If user selected all individual items manually, also add ALL_OPTION
    if (nonAllOptions.length === allAvailableOptions.length && allAvailableOptions.length > 0) {
      setter([ALL_OPTION, ...nonAllOptions]);
    } else {
      setter(nonAllOptions);
    }
  }
};

// Helper: Export chart data as CSV using API trend data
const exportChartAsCSV = (
  months: string[],
  series: { name: string; data: number[] }[]
) => {
  // Build CSV
  const headers = ["Group", ...months.map(formatMonthLabel)];
  const rows = series.map(s => {
    return [s.name, ...s.data.map(v => String(v || 0))];
  });

  const csv = [headers.join(","), ...rows.map(r => r.map(c => `"${c}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `PHIC_Trend_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};


const PHICTrendTab: React.FC = () => {
  const dispatch = useDispatch();
  const { hasDataPermission } = usePermission();
  const { data: filtersData, loading: filtersLoading, error: filtersError } = useSelector(
    (state: RootState) => state.executiveOverview.phicTrendFilters
  );
  const { data: trendData, loading: trendLoading, error: trendError } = useSelector(
    (state: RootState) => state.executiveOverview.phicTrendData
  );

  // Fetch PHIC Trend filter options on mount
  useEffect(() => {
    dispatch(getPHICTrendFiltersRequest());
  }, [dispatch]);

  // Dimension filters — Multiselect (multi-value selection)
  const [operationFilter, setOperationFilter] = useState<readonly MultiselectProps.Option[]>([]);
  const [cmFilter, setCmFilter] = useState<readonly MultiselectProps.Option[]>([]);
  const [locationFilter, setLocationFilter] = useState<readonly MultiselectProps.Option[]>([]);

  // Time range filter
  const [dateRange, setDateRange] = useState<TimeRangeValue | null>(null);

  const [groupBy, setGroupBy] = useState<SelectProps.Option>(GROUP_BY_OPTIONS[0]);
  const [colorPalette, setColorPalette] = useState<SelectProps.Option>(PALETTE_OPTIONS[2]);
  const PROGRAM_COLORS = COLOR_PALETTES[colorPalette.value || "tableau"].colors;

  // Derive startTime/endTime from dateRange
  const { startTime, endTime } = useMemo(() => {
    if (!dateRange) return { startTime: "", endTime: "" };
    return {
      startTime: dateRange.startDate.slice(0, 7),
      endTime: dateRange.endDate.slice(0, 7),
    };
  }, [dateRange]);

  // ========================
  // Cascading filter logic :
  // Operation, CM, Location are interdependent (multi-select).
  // When one is selected, the other two filter their options to only
  // those values that exist in combinations matching the current selection.
  // ========================
  const combinations: PHICTrendFilterCombination[] = useMemo(() => {
    return filtersData?.combinations || [];
  }, [filtersData]);

  const cascadedOptions = useMemo(() => {
    // When API combinations are not available, show empty options
    if (combinations.length === 0) {
      return {
        operationOptions: [ALL_OPTION],
        cmOptions: [ALL_OPTION],
        locationOptions: [ALL_OPTION],
      };
    }

    // Use API combinations for cascading filter logic (exclude __all__ sentinel)
    const opFiltered = operationFilter.filter(o => o.value !== "__all__");
    const cmFiltered = cmFilter.filter(o => o.value !== "__all__");
    const locFiltered = locationFilter.filter(o => o.value !== "__all__");
    const opValues = opFiltered.length > 0 ? opFiltered.map(o => o.value!) : null;
    const cmValues = cmFiltered.length > 0 ? cmFiltered.map(o => o.value!) : null;
    const locValues = locFiltered.length > 0 ? locFiltered.map(o => o.value!) : null;

    // Filter combinations for each dropdown based on other selections
    let combsForOp = combinations;
    let combsForCm = combinations;
    let combsForLoc = combinations;

    if (cmValues) combsForOp = combsForOp.filter(c => cmValues.includes(c.cm));
    if (locValues) combsForOp = combsForOp.filter(c => locValues.includes(c.location));

    if (opValues) combsForCm = combsForCm.filter(c => opValues.includes(c.operation));
    if (locValues) combsForCm = combsForCm.filter(c => locValues.includes(c.location));

    if (opValues) combsForLoc = combsForLoc.filter(c => opValues.includes(c.operation));
    if (cmValues) combsForLoc = combsForLoc.filter(c => cmValues.includes(c.cm));

    const availableOps = [...new Set(combsForOp.map(c => c.operation))].sort();
    const availableCms = [...new Set(combsForCm.map(c => c.cm))].sort();
    const availableLocs = [...new Set(combsForLoc.map(c => c.location))].sort();

    return {
      operationOptions: [ALL_OPTION, ...availableOps.map(v => ({ label: v, value: v }))],
      cmOptions: [ALL_OPTION, ...availableCms.map(v => ({ label: v, value: v }))],
      locationOptions: [ALL_OPTION, ...availableLocs.map(v => ({ label: v, value: v }))],
    };
  }, [combinations, operationFilter, cmFilter, locationFilter]);

  // Fetch PHIC Trend chart data when time range or groupBy changes
  // Note: Operation/CM/Location filters are applied purely on the frontend (no API call)
  useEffect(() => {
    const defaultRange = getDefaultTimeRange();
    const effStart = startTime || defaultRange.start;
    const effEnd = endTime || defaultRange.end;
    dispatch(getPHICTrendDataRequest({
      start_time: effStart,
      end_time: effEnd,
      group_by: (groupBy?.value || "program") as PHICTrendGroupBy,
    }));
  }, [dispatch, startTime, endTime, groupBy]);

  // Months from API (used for chart x-axis, scroll logic, annotations)
  const visibleMonths: string[] = trendData?.months || [];

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  // Measure container width using ResizeObserver
  // Re-run when loading/error state changes (since the container div is conditionally rendered)
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    // Defer state updates to the next frame so the observer callback returns
    // synchronously — this prevents the benign "ResizeObserver loop completed
    // with undelivered notifications" error from firing.
    let rafId = 0;
    const observer = new ResizeObserver((entries) => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        for (const entry of entries) {
          setContainerWidth(entry.contentRect.width);
        }
      });
    });
    observer.observe(el);
    // Set initial value
    setContainerWidth(el.clientWidth);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [trendLoading, trendError]);

  const MIN_BAR_WIDTH = 80;

  // Determine whether scroll is needed and the effective chart width
  const { needsScroll, chartWidth } = useMemo(() => {
    const totalMonths = visibleMonths.length;
    // When months <= MAX_VISIBLE_MONTHS, always fill container (no scroll)
    if (totalMonths <= MAX_VISIBLE_MONTHS) {
      return { needsScroll: false, chartWidth: undefined };
    }
    // When months > MAX_VISIBLE_MONTHS, check if MIN_BAR_WIDTH * totalMonths exceeds container
    const minRequiredWidth = totalMonths * MIN_BAR_WIDTH;
    if (containerWidth > 0 && minRequiredWidth <= containerWidth) {
      // The bars at min width still fit within the container, so fill the container (no scroll)
      return { needsScroll: false, chartWidth: undefined };
    }
    // Scroll is needed: use the larger of minRequiredWidth or containerWidth
    return { needsScroll: true, chartWidth: minRequiredWidth };
  }, [visibleMonths, containerWidth]);

  useEffect(() => {
    if (needsScroll && scrollContainerRef.current) {
      const el = scrollContainerRef.current;
      requestAnimationFrame(() => { el.scrollLeft = el.scrollWidth - el.clientWidth; });
    }
  }, [needsScroll, visibleMonths, trendData]);

  // Frontend filtering: filter series by Operation/CM/Location selections
  const filteredSeries: PHICTrendSeriesItem[] = useMemo(() => {
    if (!trendData || !trendData.series) return [];

    const opValues = operationFilter.filter(o => o.value !== "__all__").map(o => o.value!);
    const cmValues = cmFilter.filter(o => o.value !== "__all__").map(o => o.value!);
    const locValues = locationFilter.filter(o => o.value !== "__all__").map(o => o.value!);

    return trendData.series.filter((series) => {
      if (opValues.length > 0 && series.operation && !opValues.includes(series.operation)) return false;
      if (cmValues.length > 0 && !cmValues.includes(series.cm)) return false;
      if (locValues.length > 0 && !locValues.includes(series.location)) return false;
      return true;
    });
  }, [trendData, operationFilter, cmFilter, locationFilter]);

  const { traces, monthlyTotals } = useMemo(() => {
    // Only use API trendData; show empty chart when data is not available
    if (!trendData || !trendData.months) {
      return { traces: [], monthlyTotals: [] };
    }

    const months = trendData.months;
    const apiGroupBy = (groupBy?.value || "program") as PHICTrendGroupBy;

    // When filtered series is empty, still show months on x-axis with 0 values
    if (filteredSeries.length === 0) {
      const zeroTotals = months.map(() => 0);
      // Create a placeholder trace so Plotly renders the x-axis with month labels
      const placeholderTrace = {
        x: months,
        y: zeroTotals,
        name: "",
        type: "bar" as const,
        marker: { color: "transparent" },
        hoverinfo: "skip" as const,
        showlegend: false,
      };
      return { traces: [placeholderTrace], monthlyTotals: zeroTotals };
    }

    // Merge series with the same seriesName: sum their data arrays
    const mergedMap = new Map<string, { name: string; data: number[] }>();
    for (const series of filteredSeries) {
      let seriesName: string;
      if (apiGroupBy === "program") {
        seriesName = series.name || `${series.program}-${series.cm}-${series.location}`;
      } else if (apiGroupBy === "alert_type") {
        seriesName = series.alert_type || "Unknown";
      } else if (apiGroupBy === "cause_type") {
        seriesName = series.cause_type || "Unknown";
      } else {
        seriesName = series.name || "Unknown";
      }

      if (mergedMap.has(seriesName)) {
        const existing = mergedMap.get(seriesName)!;
        for (let i = 0; i < months.length; i++) {
          existing.data[i] = (existing.data[i] || 0) + (series.data[i] || 0);
        }
      } else {
        mergedMap.set(seriesName, { name: seriesName, data: [...series.data] });
      }
    }

    const mergedSeries = Array.from(mergedMap.values());
    const traceList = mergedSeries.map((s, idx) => {
      const color = PROGRAM_COLORS[idx % PROGRAM_COLORS.length];
      return {
        x: months,
        y: s.data,
        name: s.name,
        type: "bar" as const,
        marker: { color },
        hovertemplate:
          `<b>%{x}</b><br>` +
          `<span style="color:${color};">●</span> ${s.name}: <b>%{y}</b>` +
          `<extra></extra>`,
      };
    });

    // Always recompute totals from filtered series (API totals represent all unfiltered data)
    const totals = months.map((_: string, i: number) => {
      let total = 0;
      for (const s of mergedSeries) {
        total += s.data[i] || 0;
      }
      return total;
    });

    return { traces: traceList, monthlyTotals: totals };
  }, [trendData, PROGRAM_COLORS, groupBy, filteredSeries]);

  const barAnnotations = useMemo(() => {
    return visibleMonths.map((month, idx) => ({
      x: month,
      y: monthlyTotals[idx],
      text: String(monthlyTotals[idx]),
      xanchor: "center" as const,
      yanchor: "bottom" as const,
      showarrow: false,
      font: { size: 11, color: "#5f6b7a", family: "'Amazon Ember', 'Helvetica Neue', Roboto, Arial, sans-serif" },
    }));
  }, [visibleMonths, monthlyTotals]);

  const timeRangeDescription = useMemo(() => {
    if (visibleMonths.length > 0) {
      const firstMonth = visibleMonths[0];
      const lastMonth = visibleMonths[visibleMonths.length - 1];
      return `Showing ${visibleMonths.length} month(s): ${formatMonthLabel(firstMonth)} — ${formatMonthLabel(lastMonth)}`;
    }
    if (!startTime && !endTime) return "Showing last 12 months (default)";
    return "";
  }, [visibleMonths, startTime, endTime]);

  return (
    <SpaceBetween size="l">
      {/* Filters */}
      <Container data-testid="phic-trend-filters">
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
          <div style={{ minWidth: "180px", flex: "1 1 180px" }}>
            <FormField label="Operation">
              <Multiselect
                data-testid="filter-operation"
                selectedOptions={[...operationFilter]}
                onChange={({ detail }) => {
                  handleMultiselectWithAll(
                    detail.selectedOptions,
                    cascadedOptions.operationOptions.filter(o => o.value !== "__all__"),
                    [...operationFilter],
                    setOperationFilter
                  );
                }}
                options={cascadedOptions.operationOptions}
                placeholder="All Operations"
                tokenLimit={2}
              />
            </FormField>
          </div>
          <div style={{ minWidth: "180px", flex: "1 1 180px" }}>
            <FormField label="CM">
              <Multiselect
                data-testid="filter-cm"
                selectedOptions={[...cmFilter]}
                onChange={({ detail }) => {
                  handleMultiselectWithAll(
                    detail.selectedOptions,
                    cascadedOptions.cmOptions.filter(o => o.value !== "__all__"),
                    [...cmFilter],
                    setCmFilter
                  );
                }}
                options={cascadedOptions.cmOptions}
                placeholder="All CMs"
                tokenLimit={2}
              />
            </FormField>
          </div>
          <div style={{ minWidth: "180px", flex: "1 1 180px" }}>
            <FormField label="Location">
              <Multiselect
                data-testid="filter-location"
                selectedOptions={[...locationFilter]}
                onChange={({ detail }) => {
                  handleMultiselectWithAll(
                    detail.selectedOptions,
                    cascadedOptions.locationOptions.filter(o => o.value !== "__all__"),
                    [...locationFilter],
                    setLocationFilter
                  );
                }}
                options={cascadedOptions.locationOptions}
                placeholder="All Locations"
                tokenLimit={2}
              />
            </FormField>
          </div>
          <div style={{ minWidth: "280px", flex: "2 1 280px" }}>
            <FormField label="Time Range" data-testid="filter-date-range">
              <TimeRange
                value={dateRange}
                onChange={(value) => setDateRange(value)}
                granularity="month"
                placeholder="Select time range"
              />
            </FormField>
          </div>
        </div>
      </Container>

      {/* Stacked Bar Chart */}
      <Container
        data-testid="phic-trend-chart-container"
        header={
          <Header
            variant="h2"
            description={<span data-testid="chart-time-description">{timeRangeDescription}</span>}
            actions={
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{ fontSize: "14px", fontWeight: 500, whiteSpace: "nowrap" }}>Category (in MSA) - by</span>
                <div style={{ minWidth: "150px" }}>
                  <Select
                    data-testid="chart-group-by"
                    selectedOption={groupBy}
                    onChange={({ detail }) => setGroupBy(detail.selectedOption as SelectProps.Option)}
                    options={GROUP_BY_OPTIONS}
                  />
                </div>
                {/* Color palette selector — hidden for now
                <div style={{ minWidth: "150px" }}>
                  <Select
                    selectedOption={colorPalette}
                    onChange={({ detail }) => setColorPalette(detail.selectedOption as SelectProps.Option)}
                    options={PALETTE_OPTIONS}
                  />
                </div>
                */}
                <ButtonDropdown
                  data-testid="chart-export"
                  items={[
                    { id: "csv", text: "Export CSV", iconName: "download" },
                    // { id: "image", text: "Export Image (PNG)", iconName: "file" },
                  ]}
                  onItemClick={({ detail }) => {
                    if (detail.id === "csv") {
                      if (trendData && trendData.months && trendData.series) {
                        const csvSeries = traces.map(t => ({ name: t.name, data: t.y as number[] }));
                        exportChartAsCSV(trendData.months, csvSeries);
                      }
                    } else if (detail.id === "image") {
                      // Export chart as PNG by layering ALL SVGs from the Plotly plot
                      const container = scrollContainerRef.current;
                      if (container) {
                        const plotDiv = container.querySelector(".js-plotly-plot") as HTMLElement;
                        if (plotDiv) {
                          const svgElements = plotDiv.querySelectorAll("svg");
                          if (svgElements.length > 0) {
                            const plotBbox = plotDiv.getBoundingClientRect();
                            const canvasW = plotBbox.width;
                            const canvasH = plotBbox.height;
                            const scale = 2;
                            const canvas = document.createElement("canvas");
                            canvas.width = canvasW * scale;
                            canvas.height = canvasH * scale;
                            const ctx = canvas.getContext("2d");
                            if (!ctx) return;
                            ctx.scale(scale, scale);
                            ctx.fillStyle = "#ffffff";
                            ctx.fillRect(0, 0, canvasW, canvasH);

                            // Render each SVG layer onto the canvas sequentially
                            let loaded = 0;
                            const totalSvgs = svgElements.length;
                            const images: { img: HTMLImageElement; x: number; y: number; w: number; h: number }[] = [];

                            svgElements.forEach((svg, idx) => {
                              const svgBbox = svg.getBoundingClientRect();
                              const clone = svg.cloneNode(true) as SVGSVGElement;
                              clone.setAttribute("width", String(svgBbox.width));
                              clone.setAttribute("height", String(svgBbox.height));
                              clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
                              clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
                              // Inline styles for all elements
                              const cloneEls = clone.querySelectorAll("*");
                              const origEls = svg.querySelectorAll("*");
                              cloneEls.forEach((el, i) => {
                                if (i < origEls.length) {
                                  const computed = window.getComputedStyle(origEls[i]);
                                  ["fill", "stroke", "stroke-width", "font-size", "font-family", "font-weight", "opacity", "transform", "display", "visibility", "text-anchor", "dominant-baseline"].forEach(prop => {
                                    const val = computed.getPropertyValue(prop);
                                    if (val && val !== "none" && val !== "normal") {
                                      (el as HTMLElement).style.setProperty(prop, val);
                                    }
                                  });
                                }
                              });
                              const svgData = new XMLSerializer().serializeToString(clone);
                              const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
                              const url = URL.createObjectURL(blob);
                              const img = new Image();
                              const offsetX = svgBbox.left - plotBbox.left;
                              const offsetY = svgBbox.top - plotBbox.top;
                              images[idx] = { img, x: offsetX, y: offsetY, w: svgBbox.width, h: svgBbox.height };
                              img.onload = () => {
                                loaded++;
                                URL.revokeObjectURL(url);
                                if (loaded === totalSvgs) {
                                  // Draw all layers in order
                                  images.forEach(({ img: i, x, y, w, h }) => {
                                    ctx.drawImage(i, x, y, w, h);
                                  });
                                  const pngUrl = canvas.toDataURL("image/png");
                                  const link = document.createElement("a");
                                  link.href = pngUrl;
                                  link.download = `PHIC_Trend_${new Date().toISOString().slice(0, 10)}.png`;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }
                              };
                              img.onerror = () => {
                                loaded++;
                                URL.revokeObjectURL(url);
                                if (loaded === totalSvgs) {
                                  // Still try to export what we have
                                  images.forEach(({ img: i, x, y, w, h }) => {
                                    if (i.complete && i.naturalWidth > 0) {
                                      ctx.drawImage(i, x, y, w, h);
                                    }
                                  });
                                  const pngUrl = canvas.toDataURL("image/png");
                                  const link = document.createElement("a");
                                  link.href = pngUrl;
                                  link.download = `PHIC_Trend_${new Date().toISOString().slice(0, 10)}.png`;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }
                              };
                              img.src = url;
                            });
                          }
                        }
                      }
                    }
                  }}
                  variant="icon"
                  ariaLabel="Export chart"
                >
                  Export
                </ButtonDropdown>
              </div>
            }
          >
            PHIC Monthly Trend
            {needsScroll && (
              <span data-testid="chart-scroll-hint" style={{ fontSize: "12px", fontWeight: 400, color: "#5f6b7a", marginLeft: "8px" }}>
                (scroll horizontally to view all {visibleMonths.length} months)
              </span>
            )}
          </Header>
        }
      >
        {/* Loading state */}
        {trendLoading && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 20px", gap: "12px" }}>
            <Spinner size="large" />
            <span style={{ fontSize: "14px", color: "#5f6b7a" }}>Loading PHIC Monthly Trend...</span>
          </div>
        )}

         {/* Error state — displayed as inline empty state with error message */}
        {!trendLoading && trendError && (
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 20px",
            gap: "12px",
            textAlign: "center",
          }}>
            <div style={{display:'flex',alignItems:'center'}}>
              <Icon name="status-negative" size="big" variant="error" />
              <Box variant="strong" fontSize="heading-m">
                <span style={{color:'#e11212',marginLeft:'12px',fontSize:'40px'}}>Failed to load chart data</span>
              </Box>
            </div>
            <Box variant="p" color="text-body-secondary" fontSize="body-m">
              <span style={{color:'#f62121'}}>
                {trendError}
              </span>
            </Box>
          </div>
        )}

        {/* Chart */}
        {!trendLoading && !trendError && (
        <div
          ref={scrollContainerRef}
          data-testid="chart-scroll-area"
          style={{
            overflowX: needsScroll ? "auto" : "hidden",
            overflowY: "hidden",
            width: "100%",
            scrollbarWidth: "thin",
            scrollbarColor: "#b4b4b4 #f0f0f0",
          }}
        >
          <Plot
            data={traces}
            layout={{
              barmode: "stack",
              bargap: visibleMonths.length <= 3 ? 0.6 : visibleMonths.length <= 6 ? 0.5 : 0.3,
              hovermode: "closest",
              hoverlabel: {
                bgcolor: "#ffffff",
                bordercolor: "#d5dbdb",
                font: { family: "'Amazon Ember', 'Helvetica Neue', Roboto, Arial, sans-serif", size: 13, color: "#16191f" },
                align: "left",
                namelength: -1,
              },
              xaxis: {
                title: "",
                tickangle: -45,
                tickmode: "array",
                tickvals: visibleMonths,
                ticktext: visibleMonths.map(formatMonthLabel),
                fixedrange: true,
              },
              yaxis: {
                title: "Quantity",
                fixedrange: true,
                rangemode: "tozero",
                ...(monthlyTotals.length > 0 && Math.max(...monthlyTotals) === 0
                  ? { range: [0, 1] }
                  : {}),
              },
              legend: {
                orientation: "h",
                y: -0.18,
                x: 0.5,
                xanchor: "center",
                yanchor: "top",
                font: { size: 10 },
                tracegroupgap: 2,
                itemwidth: 30,
              },
              annotations: barAnnotations,
              margin: { t: 25, b: 120, l: 50, r: 20 },
              height: 700,
              width: chartWidth,
              autosize: !needsScroll,
            }}
            config={{ responsive: !needsScroll, displayModeBar: false, staticPlot: false }}
            style={{ width: chartWidth ? `${chartWidth}px` : "100%" }}
          />
        </div>
        )}
      </Container>
    </SpaceBetween>
  );
};

// ========================
// Main Component
// ========================
const ExecutiveOverview: React.FC = () => {
  const { hasPermission } = usePermission();

  const [activeTabId, setActiveTabId] = useState("cm-performance");
  const [programParams, setProgramParams] = useState<{ program?: string; cm?: string; operation?: string; location?: string; date?: string } | null>(null);

  const handleNavigateToProgram = useCallback((params: Record<string, string>) => {
    setProgramParams(params);
    setActiveTabId("program-overview");
  }, []);

  const handleTabChange = useCallback(({ detail }: { detail: { activeTabId: string } }) => {
    setProgramParams(null);
    setActiveTabId(detail.activeTabId);
  }, []);

  const tabs = useMemo(() => {
    const items: Array<{ label: string; id: string; content: React.ReactNode }> = [];
    if (hasPermission("executive_overview.eo_view_cm")) {
      items.push({ label: "CM Overview", id: "cm-performance", content: <CMPerformanceTab onNavigateToProgram={handleNavigateToProgram} /> });
    }
    if (hasPermission("executive_overview.eo_view_program")) {
      items.push({ label: "Program Overview", id: "program-overview", content: <Program {...(programParams || {})} /> });
    }
    if (hasPermission("executive_overview.eo_view_phic")) {
      items.push({ label: "PHIC Trend", id: "phic-trend", content: <PHICTrendTab /> });
    }
    return items;
  }, [hasPermission, handleNavigateToProgram, programParams]);

  return (
    <SpaceBetween size="l" data-testid="executive-overview-page">
      <Header variant="h1"  data-testid="executive-overview-header">
        Monitor & Alert
      </Header>

      <Tabs
        data-testid="executive-overview-tabs"
        activeTabId={activeTabId}
        onChange={handleTabChange}
        tabs={tabs}
      />
    </SpaceBetween>
  );
};

export default ExecutiveOverview;
